/**
 * Operations Domain State
 *
 * The operations domain drives the generic propose → review → apply lifecycle
 * (TV1-1828). It has two concerns:
 *
 * - `review`: the current `OperationRun` being reviewed — its lifecycle metadata
 *   (from `OperationRunsReadmodel`), the full computed change set (from
 *   `OperationChangeSetReadmodel`), and the per-row approval selection that the
 *   operator toggles before approving.
 * - `list`: the run history (lifecycle rows only — no change-set payload).
 *
 * The state mirrors the shared integration types (`OperationRunsReadmodelType`,
 * `OperationChangeSetReadmodelType`, `ChangeItemType`) — it never redefines the
 * `ChangeItem` shape or its statuses.
 */

import {
  OperationRunsReadmodelType,
  OperationChangeSetReadmodelType,
  ChangeItemType
} from '@bett3r-dev/teselly-domain';

/**
 * Loading states for the operations surface.
 */
export type OperationsLoadingStates = {
  /** The current run's lifecycle row is being loaded/subscribed. */
  run: boolean;
  /** The change-set payload for the current run is being loaded. */
  changeSet: boolean;
  /** An approval/reject submission is in flight. */
  submitting: boolean;
  /** The run history list is being loaded. */
  history: boolean;
};

/**
 * Review subdomain — the run currently under review plus its change set and the
 * operator's per-row approval selection.
 */
export type OperationsReviewState = {
  /** The run being reviewed (lifecycle metadata + counts + status). */
  run: OperationRunsReadmodelType | null;

  /** The full computed change set keyed to the current run (the diff grid source). */
  changeSet: OperationChangeSetReadmodelType | null;

  /**
   * Per-row approval flags keyed by `ChangeItem.targetId`.
   * `true` = approved (included in `approvedItemIds`); absent/`false` = excluded.
   * Rows default to approved when the change set loads (all checked), except
   * non-actionable rows (`skipped` / `rejected`), which default to excluded, and
   * rows the operation explicitly opted out of via `ChangeItem.preselected`
   * (TV1-2113) — see `buildDefaultRowApproval`.
   */
  rowApproval: Record<string, boolean>;
};

/**
 * List subdomain — the operation-run history.
 */
export type OperationsListState = {
  /** Run-history rows (lifecycle only). */
  runs: OperationRunsReadmodelType[];

  /** Total count of history runs (for pagination). */
  runsCount: number;

  /** Current page number (1-based). */
  currentPage: number;

  /** Runs per page. */
  perPage: number;
};

/**
 * Main operations domain state — `review` and `list` concerns plus loading.
 */
export type OperationsState = {
  review: OperationsReviewState;
  list: OperationsListState;
  loading: OperationsLoadingStates;
};

export const initialState: OperationsState = {
  review: {
    run: null,
    changeSet: null,
    rowApproval: {}
  },
  list: {
    runs: [],
    runsCount: 0,
    currentPage: 1,
    perPage: 10
  },
  loading: {
    run: false,
    changeSet: false,
    submitting: false,
    history: false
  }
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * A change item is actionable (can be applied) when it is in a `proposed` or
 * `approved` status. `skipped` / `rejected` / `applied` / `failed` rows are
 * never part of a fresh approval submission.
 */
export const isActionableItem = ( item: ChangeItemType ): boolean =>
  item.status === 'proposed' || item.status === 'approved';

/**
 * Build the default per-row approval map for a change set.
 *
 * Base rule (unchanged since TV1-1828): every actionable row defaults to
 * approved (checked); non-actionable rows default to excluded.
 *
 * TV1-2113 (D4) adds an OPTIONAL per-item `preselected` opinion:
 *
 *  - `preselected === undefined` → the operation has no opinion, so the base
 *    rule applies. This is what keeps `reprice` and `bulk-publish` byte-identical
 *    — they never set the flag, so nothing about their grid changes.
 *  - `preselected` set → the operation decides, but ONLY within what is already
 *    actionable. The actionable check is a hard AND: a `skipped` row is never
 *    ticked, whatever the flag says, because a skipped row cannot be applied at
 *    all and ticking it would put an un-appliable target into `approvedItemIds`.
 *
 * The decision is made PER ITEM, not "if any item carries the flag then all
 * items use it": `map-channel-categories` proposes a mix of confident picks and
 * rows the seller must look at, and a whole-set switch would collapse exactly
 * the distinction the flag exists to express.
 *
 * ⚠️ Pre-selection is a UI DEFAULT, never a commit. It only decides what the
 * grid starts from; `ApproveOperation` is still a human act, and the operator
 * can flip any row before submitting.
 */
export const buildDefaultRowApproval = (
  changeSet: OperationChangeSetReadmodelType | null
): Record<string, boolean> => {
  if ( !changeSet ) return {};
  return changeSet.items.reduce<Record<string, boolean>>(( acc, item ) => {
    acc[item.targetId] =
      isActionableItem( item ) && ( item.preselected ?? true );
    return acc;
  }, {});
};

// =============================================================================
// SELECTORS
// =============================================================================

export const selectors = {
  /** All change items for the run under review (empty when nothing loaded). */
  changeItems: ( state: OperationsState ): ChangeItemType[] =>
    state.review.changeSet?.items ?? [],

  /** Items that may be approved/applied (`proposed` / `approved`). */
  actionableItems: ( state: OperationsState ): ChangeItemType[] =>
    selectors.changeItems( state ).filter( isActionableItem ),

  /** Whether a given target row is currently approved. */
  isRowApproved: ( state: OperationsState, targetId: string ): boolean =>
    state.review.rowApproval[targetId] === true,

  /**
   * The approved subset of change items — the actionable items whose row is
   * currently checked. Load-bearing: this is the source of `approvedItemIds`
   * for the approval dispatch (T21).
   */
  approvedItems: ( state: OperationsState ): ChangeItemType[] =>
    selectors
      .actionableItems( state )
      .filter(( item ) => state.review.rowApproval[item.targetId] === true ),

  /**
   * The approved `targetId`s — exactly what feeds `ApproveOperation`'s
   * `approvedItemIds`. Order follows change-set order for stable hashing.
   */
  approvedItemIds: ( state: OperationsState ): string[] =>
    selectors.approvedItems( state ).map(( item ) => item.targetId ),

  /** Count of approved rows. */
  approvedCount: ( state: OperationsState ): number =>
    selectors.approvedItems( state ).length,

  /** Whether every actionable row is currently approved. */
  isAllApproved: ( state: OperationsState ): boolean => {
    const actionable = selectors.actionableItems( state );
    if ( actionable.length === 0 ) return false;
    return actionable.every(( item ) => state.review.rowApproval[item.targetId] === true );
  },

  /** Whether at least one actionable row is approved (submit gate). */
  hasApproval: ( state: OperationsState ): boolean =>
    selectors.approvedCount( state ) > 0,

  /** Number of items applied so far for the run under review. */
  appliedCount: ( state: OperationsState ): number =>
    state.review.run?.appliedCount ?? 0,

  /** Number of items that failed to apply for the run under review. */
  failedCount: ( state: OperationsState ): number =>
    state.review.run?.failedCount ?? 0,

  /** Total items in the computed change set for the run under review. */
  totalItems: ( state: OperationsState ): number =>
    state.review.run?.totalItems ?? selectors.changeItems( state ).length,

  /**
   * Apply progress as a 0–100 percentage based on
   * `(appliedCount + failedCount) / totalItems`. Mirrors the batch-process
   * progress derivation. Returns 0 when total is unknown.
   */
  applyProgressPercentage: ( state: OperationsState ): number => {
    const total = selectors.totalItems( state );
    if ( !total ) return 0;
    const done = selectors.appliedCount( state ) + selectors.failedCount( state );
    return Math.round(( done / total ) * 100 );
  },

  /** Whether the run under review is in a terminal status. */
  isTerminal: ( state: OperationsState ): boolean => {
    const status = state.review.run?.status;
    return (
      status === 'applied' ||
      status === 'partially-applied' ||
      status === 'failed' ||
      status === 'rejected'
    );
  },

  /** Whether the run is `ready` (computed and awaiting review/approval). */
  isReadyForReview: ( state: OperationsState ): boolean =>
    state.review.run?.status === 'ready',

  /** Total number of history pages (>= 1). */
  totalHistoryPages: ( state: OperationsState ): number =>
    Math.max( Math.ceil( state.list.runsCount / state.list.perPage ), 1 )
};
